import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Shape } from '../types/Shape';

export interface UseCanvasViewportProps {
  width: number;
  height: number;
  shapes: Shape[];
  canvasContainerRef: React.RefObject<HTMLDivElement>;
}

export interface UseCanvasViewportReturn {
  // Position & Scale
  stagePos: { x: number; y: number };
  setStagePos: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  stageScale: number;
  setStageScale: React.Dispatch<React.SetStateAction<number>>;

  // Computed canvas size and bounds
  canvasSize: { width: number; height: number };
  canvasBounds: { minX: number; minY: number; maxX: number; maxY: number };

  // Dragging state
  isCanvasDragging: boolean;
  setIsCanvasDragging: React.Dispatch<React.SetStateAction<boolean>>;
  isWheelPanning: boolean;
  setIsWheelPanning: React.Dispatch<React.SetStateAction<boolean>>;
  canvasDragStart: { x: number; y: number } | null;
  setCanvasDragStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;

  // Refs
  canvasDragRafRef: React.MutableRefObject<number | null>;

  // Touch refs (for pinch zoom)
  lastTouchDistanceRef: React.MutableRefObject<number | null>;
  lastTouchCenterRef: React.MutableRefObject<{ x: number; y: number } | null>;
  lastSingleTouchPosRef: React.MutableRefObject<{ x: number; y: number } | null>;
  isTouchPanningRef: React.MutableRefObject<boolean>;

  // Handlers
  handleWheel: (e: any) => void;
  handleStageDoubleClick: (e: any) => void;
  handleCanvasMouseLeave: () => void;

  // Utility
  screenToCanvas: (screenX: number, screenY: number) => { x: number; y: number };
  canvasToScreen: (canvasX: number, canvasY: number) => { x: number; y: number };
}

export function useCanvasViewport({
  width,
  height,
  shapes,
  canvasContainerRef,
}: UseCanvasViewportProps): UseCanvasViewportReturn {
  // Position & Scale
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);

  // Dragging state
  const [isCanvasDragging, setIsCanvasDragging] = useState(false);
  const [isWheelPanning, setIsWheelPanning] = useState(false);
  const [canvasDragStart, setCanvasDragStart] = useState<{ x: number; y: number } | null>(null);

  // Refs
  const canvasDragRafRef = useRef<number | null>(null);

  // Touch refs
  const lastTouchDistanceRef = useRef<number | null>(null);
  const lastTouchCenterRef = useRef<{ x: number; y: number } | null>(null);
  const lastSingleTouchPosRef = useRef<{ x: number; y: number } | null>(null);
  const isTouchPanningRef = useRef(false);

  // Computed canvas size and bounds (dynamic based on shapes)
  const { canvasSize, canvasBounds } = useMemo(() => {
    const padding = 2000;

    if (shapes.length === 0) {
      return {
        canvasSize: { width: width + padding * 2, height: height + padding * 2 },
        canvasBounds: { minX: -padding, minY: -padding, maxX: width + padding, maxY: height + padding }
      };
    }

    let minX = 0;
    let minY = 0;
    let maxX = width;
    let maxY = height;

    shapes.forEach(shape => {
      const shapeLeft = shape.x;
      const shapeTop = shape.y;
      const shapeRight = shape.x + (shape.width || shape.radius || 100);
      const shapeBottom = shape.y + (shape.height || shape.radius || 100);

      if (shapeLeft < minX) minX = shapeLeft;
      if (shapeTop < minY) minY = shapeTop;
      if (shapeRight > maxX) maxX = shapeRight;
      if (shapeBottom > maxY) maxY = shapeBottom;
    });

    // Add padding
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    return {
      canvasSize: { width: maxX - minX, height: maxY - minY },
      canvasBounds: { minX, minY, maxX, maxY }
    };
  }, [shapes, width, height]);

  // Wheel handler (Ctrl+Wheel: zoom, Regular wheel: scroll)
  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();

    const stage = e.target.getStage();
    if (!stage) return;

    // Ctrl + wheel: zoom
    if (e.evt.ctrlKey) {
      const scaleBy = 1.1;
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };

      const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
      setStageScale(newScale);

      const newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };

      setStagePos(newPos);
    }
    // Regular wheel: scroll (canvas movement)
    else {
      // Middle mouse button drag start
      if (e.evt.buttons === 4) {
        setIsWheelPanning(true);
        if (stage.container()) {
          stage.container().style.cursor = 'grabbing';
        }
      }

      const scrollSpeed = 1;
      const newPos = {
        x: stage.x() - e.evt.deltaX * scrollSpeed,
        y: stage.y() - e.evt.deltaY * scrollSpeed,
      };
      setStagePos(newPos);
    }
  }, []);

  // Double click on middle button: reset scale
  const handleStageDoubleClick = useCallback((e: any) => {
    if (e.evt.button === 1) {
      e.cancelBubble = true;
      setStageScale(1);
      setStagePos({ x: 0, y: 0 });
    }
  }, []);

  // Utility: Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((screenX: number, screenY: number) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return { x: screenX, y: screenY };

    return {
      x: (screenX - rect.left - stagePos.x) / stageScale,
      y: (screenY - rect.top - stagePos.y) / stageScale,
    };
  }, [stagePos, stageScale, canvasContainerRef]);

  // Utility: Convert canvas coordinates to screen coordinates
  const canvasToScreen = useCallback((canvasX: number, canvasY: number) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return { x: canvasX, y: canvasY };

    return {
      x: canvasX * stageScale + stagePos.x + rect.left,
      y: canvasY * stageScale + stagePos.y + rect.top,
    };
  }, [stagePos, stageScale, canvasContainerRef]);

  // Handle canvas mouse leave - stop wheel panning
  const handleCanvasMouseLeave = useCallback(() => {
    if (isCanvasDragging && isWheelPanning) {
      if (canvasDragRafRef.current !== null) {
        cancelAnimationFrame(canvasDragRafRef.current);
      }
      setIsWheelPanning(false);
      setIsCanvasDragging(false);
      setCanvasDragStart(null);
    }
  }, [isCanvasDragging, isWheelPanning]);

  // Prevent browser zoom on Ctrl+Wheel
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    const handleWheelPrevent = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    container.addEventListener('wheel', handleWheelPrevent, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelPrevent);
    };
  }, [canvasContainerRef]);

  return {
    // Position & Scale
    stagePos,
    setStagePos,
    stageScale,
    setStageScale,

    // Computed
    canvasSize,
    canvasBounds,

    // Dragging state
    isCanvasDragging,
    setIsCanvasDragging,
    isWheelPanning,
    setIsWheelPanning,
    canvasDragStart,
    setCanvasDragStart,

    // Refs
    canvasDragRafRef,

    // Touch refs
    lastTouchDistanceRef,
    lastTouchCenterRef,
    lastSingleTouchPosRef,
    isTouchPanningRef,

    // Handlers
    handleWheel,
    handleStageDoubleClick,
    handleCanvasMouseLeave,

    // Utility
    screenToCanvas,
    canvasToScreen,
  };
}
